import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  output,
} from '@angular/core';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  HeaderConfig,
  QaiButtonComponent,
  QaiDialogComponent,
  QaiTableComponent,
  TableRow,
} from '@qai/ui';
import { IParsedUser } from 'src/app/interfaces/self-serve/assignment-manager/iteams-with-users-parser';

type TableCellClickEvent = { rowData: TableRow; columnName: string; event: Event };

@Component({
  selector: 'assignment-users-confirmation-modal',
  standalone: true,
  templateUrl: './assignment-users-confirmation-modal.component.html',
  styleUrls: ['./assignment-users-confirmation-modal.component.scss'],
  imports: [
    CommonModule,
    TranslocoModule,
    QaiDialogComponent,
    QaiButtonComponent,
    QaiTableComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AssignmentUsersConfirmationModalComponent implements OnChanges {
  @ViewChild('dialog') qaiDialogRef: QaiDialogComponent | undefined;

  @Input() open: boolean = false;
  @Input() isCreateAssignmentFlow: boolean = false;
  @Input() selectedUsers: IParsedUser[] = [];
  @Input() networkRequestInProgress: boolean = false;

  @Output() confirmClicked = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();
  @Output() cancelClicked = new EventEmitter<void>();
  readonly userRemoved = output<string>();

  modalHeading = '';
  confirmButtonText = '';

  tableData: TableRow[] = [];
  currentPage = 1;
  pageSize = 10;
  private readonly FIXED_HEIGHT = 300;
  private readonly ROW_HEIGHT = 56;

  headers: HeaderConfig[] = [];

  constructor(private translocoService: TranslocoService) {}

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.open) {
      this.calculatePageSize();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.headers.length) {
      // NOTE: 'Name', 'Email', 'Team' are used as data-row keys by QaiTable — must stay as English literals.
      this.headers = [
        { name: 'Name', sortable: true },
        { name: 'Email', sortable: true },
        { name: 'Team', sortable: true },
        { name: '', sortable: false, clickable: true },
      ];
    }

    if (changes['isCreateAssignmentFlow']) {
      this.modalHeading = this.isCreateAssignmentFlow
        ? this.translocoService.translate('simulationBuilder.CONFIRMATION_CREATE_HEADING')
        : this.translocoService.translate('simulationBuilder.CONFIRMATION_ADD_USERS_HEADING');
      this.confirmButtonText = this.isCreateAssignmentFlow
        ? this.translocoService.translate('simulationBuilder.CONFIRMATION_SAVE_ASSIGNMENT_BTN')
        : this.translocoService.translate('simulationBuilder.BUTTON_ADD_TO_ASSIGNMENT');
    }

    if (changes['open']) {
      if (changes['open'].currentValue) {
        this.currentPage = 1;
        this.calculatePageSize();
        this.buildTableData();
        setTimeout(() => this.qaiDialogRef?.showModal());
      } else {
        this.qaiDialogRef?.close();
      }
    }

    if (changes['selectedUsers']) {
      this.buildTableData();
      const totalPages = Math.ceil(this.tableData.length / this.pageSize);
      if (totalPages > 0 && this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }
    }
  }

  private calculatePageSize(): void {
    const availableHeight = window.innerHeight * 0.9 - this.FIXED_HEIGHT;
    this.pageSize = Math.min(10, Math.max(3, Math.floor(availableHeight / this.ROW_HEIGHT)));
  }

  private buildTableData(): void {
    const removeLabel = this.translocoService.translate('simulationBuilder.CONFIRMATION_REMOVE_USER');
    this.tableData = this.selectedUsers.map((user) => ({
      id: user.uuid,
      Name: { type: 'text' as const, value: user.name },
      Email: { type: 'text' as const, value: user.email },
      Team: { type: 'text' as const, value: user.teamName },
      '': {
        type: 'text' as const,
        value: removeLabel,
        kind: 'error' as const,
        weight: 'semi-bold' as const,
        clickable: true,
      },
    } as unknown as TableRow));
  }

  onPageChanged(event: { page: number; pageSize: number }): void {
    this.currentPage = event.page;
  }

  onCellClick({ rowData, event }: TableCellClickEvent): void {
    const removeLabel = this.translocoService.translate('simulationBuilder.CONFIRMATION_REMOVE_USER');
    const target = event.target as HTMLElement;
    if (target.tagName !== 'TD' && target.textContent?.trim() === removeLabel) {
      this.userRemoved.emit(rowData.id as string);
    }
  }

  onConfirm(): void {
    this.confirmClicked.emit();
  }

  onBack(): void {
    this.closeModal.emit();
  }

  onCancel(): void {
    this.cancelClicked.emit();
  }
}
